import { Link, useNavigate } from "react-router-dom";
import { createArticle, listArticles, type AdminArticle } from "@/api/admin";
import { useAction, useAsync } from "@/lib/useAsync";
import { when } from "@/lib/format";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Notice } from "@/components/ui/feedback";
import { PageHeader } from "@/components/ui/page-header";
import { SkeletonRows } from "@/components/ui/skeleton";
import { TBody, TD, TEmpty, TH, THead, TR, TableShell } from "@/components/ui/table";

/**
 * Everything written for the articles section.
 *
 * AN ARTICLE IS ONE THING IN SEVERAL LANGUAGES, not several articles. The id is
 * shared; the slug, title and words are per language, and publishing is a
 * property of the whole. That is why this list has a row per article with the
 * languages inside it rather than a row per translation -- a table of thirty
 * rows for ten articles makes "is this published" a question with ten answers
 * that must agree.
 *
 * A NEW ARTICLE IS AN EMPTY DRAFT. Creating one writes a row with no words in
 * it and opens the editor; the worker will not publish an article that has
 * nothing in any language, so an abandoned one is invisible rather than broken.
 */
export function ArticlesScreen() {
  const { data, loading, error, reload } = useAsync(() => listArticles(), []);
  const action = useAction();
  const navigate = useNavigate();

  const articles = data?.articles ?? [];

  async function start() {
    let id: string | null = null;
    await action.run(async () => {
      const created = await createArticle();
      id = created.id;
      return created;
    });
    reload();
    // Straight into the editor: an article with no words is not something to
    // admire in a list.
    if (id) navigate(`/articles/${id}`);
  }

  return (
    <div className="space-y-4">
      <PageHeader title="Articles" description="One article, any number of languages.">
        <Button size="sm" disabled={action.busy} onClick={() => void start()}>
          {action.busy ? "Creating…" : "Write a new one"}
        </Button>
      </PageHeader>

      {error && <Notice tone="error">{error}</Notice>}
      {action.error && <Notice tone="error">{action.error}</Notice>}

      <Card>
        {loading && articles.length === 0 ? (
          <SkeletonRows rows={4} cols={4} />
        ) : (
          <TableShell>
            <THead>
              <TH>Title</TH>
              <TH>Languages</TH>
              <TH>State</TH>
              <TH>Changed</TH>
            </THead>
            <TBody>
              {articles.length === 0 && <TEmpty colSpan={4}>Nothing written yet.</TEmpty>}
              {articles.map((article) => (
                <ArticleRow key={article.id} article={article} />
              ))}
            </TBody>
          </TableShell>
        )}
      </Card>
    </div>
  );
}

function ArticleRow({ article }: { article: AdminArticle }) {
  // The first language written is the one the list can name it by. An article
  // with none has no title at all, which is a fact rather than a gap.
  const first = article.locales[0];

  return (
    <TR>
      <TD>
        {/* The link is the focusable thing, not the row -- the same choice the
            Orders table makes, for the same reason. */}
        <Link
          to={`/articles/${encodeURIComponent(article.id)}`}
          className="rounded font-medium underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {first ? first.title : <span className="text-muted-foreground">Nothing written yet</span>}
        </Link>
      </TD>
      <TD>
        <div className="flex flex-wrap gap-1">
          {article.locales.length === 0 && <span className="text-muted-foreground">—</span>}
          {article.locales.map((l) => (
            <Badge key={l.locale} color="gray">
              {l.locale}
            </Badge>
          ))}
        </div>
      </TD>
      <TD>
        {article.status === "published" ? (
          <Badge color="green">published</Badge>
        ) : (
          <Badge color="amber">draft</Badge>
        )}
      </TD>
      <TD className="whitespace-nowrap text-muted-foreground">{when(article.updatedAt)}</TD>
    </TR>
  );
}
